import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import cn from '@/utils/cn';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Portal>
    {/* Content is nested inside the Overlay and is itself the scroll container.
        Three constraints have to hold at once, and this is the only shape that
        satisfies all three:

        1. A panel taller than the viewport must scroll to its footer. The
           original `-translate-y-1/2` centring overflowed BOTH edges with the
           body scroll-locked, so footer buttons were unreachable.
        2. Whatever scrolls must be a node react-remove-scroll permits. Radix
           mounts RemoveScroll on the Overlay with `shards: [contentRef]` (see
           react-dialog dist: "Make sure `Content` is scrollable even when it
           doesn't live inside `RemoveScroll`"). Its `handleScroll` only sums
           the overflow of nodes between the event target and that shard, so a
           scroll container ABOVE Content is never counted - `availableScroll`
           comes out 0 and the wheel/touch event is preventDefault'd. Putting
           `overflow-y-auto` on Content itself keeps the scroller inside the
           shard, which is also why callers may pass their own `max-h-*` /
           `overflow-*` here and have it work.
        3. No transform on Content. A transform makes it the containing block
           for `position: fixed` descendants, which is how the non-portalled
           Combobox popovers used here (`portalled={false}`) got clipped once
           an `overflow` was introduced. Hence flex centring on the Overlay
           rather than `-translate-1/2`. */}
    <DialogPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm',
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      )}
    >
      {/* The vertical gutter lives in Content's `max-h` calc, not in padding on
          the Overlay: padding would crop a panel that opts into a taller
          `max-h-*` instead of letting it scroll. */}
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'relative grid max-h-[calc(100dvh-2rem)] w-full max-w-lg gap-4 overflow-y-auto overscroll-contain',
          'rounded-xl border bg-card p-4 shadow-lg duration-200 sm:p-6',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close className="absolute right-2 top-2 grid size-11 place-items-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-1 focus:ring-ring sm:right-3 sm:top-3 sm:size-9">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Overlay>
  </DialogPrimitive.Portal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

export function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  // `pr-10` keeps the title clear of the absolutely-positioned close button.
  return <div className={cn('flex flex-col space-y-1.5 pr-10 text-left', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-lg font-bold leading-none tracking-tight', className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;
